import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Check, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { submitInquiry } from '../lib/api';
import { TID } from '../lib/testIds';

const ROOM_TYPES = ['Deluxe Room', 'Shrine-View Suite', 'Family Residence', 'No preference'];
const CITIES = [
  { value: 'Karbala', tid: TID.inquiryFieldCity_Karbala },
  { value: 'Najaf', tid: TID.inquiryFieldCity_Najaf },
  { value: 'Kadhimiya', tid: TID.inquiryFieldCity_Kadhimiya },
  { value: 'Samarra', tid: TID.inquiryFieldCity_Samarra },
];
const SERVICES = [
  { value: 'Hotel Stay', tid: TID.inquiryFieldService_Hotel },
  { value: 'Visa Concierge', tid: TID.inquiryFieldService_Visa },
  { value: 'Transportation', tid: TID.inquiryFieldService_Transport },
  { value: 'Complete Ziarat Package', tid: TID.inquiryFieldService_Package },
];

const STEPS = [
  { id: 1, label: 'You', title: 'Your details', hint: 'How may we address you?' },
  { id: 2, label: 'Group', title: 'Your group & rooms', hint: 'Pilgrims and room arrangement.' },
  { id: 3, label: 'Journey', title: 'Your journey', hint: 'Dates, cities, and services.' },
  { id: 4, label: 'Review', title: 'Almost done', hint: 'A note for us, then a final look.' },
];

const initialForm = {
  guest_name: '', contact: '', email: '',
  city_of_origin: '', nationality: '',
  requested_checkin: null, requested_checkout: null,
  pax: '', child_count: '',
  room_count: '', bed_count_preference: '', room_type: '',
  cities_to_visit: [], services_needed: [],
  notes: '',
};

const inputClass = 'w-full bg-white border border-line text-ink placeholder:text-ink-muted/40 px-4 py-5 rounded-none focus-visible:ring-2 focus-visible:ring-[color:var(--emerald)] focus-visible:ring-offset-0';
const labelClass = 'eyebrow !text-emerald-brand mb-2 block';

export default function StepperForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [furthestStep, setFurthestStep] = useState(1);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const stepValid = useMemo(() => {
    if (step === 1) return form.guest_name.trim().length >= 2 && form.contact.trim().length >= 4;
    return true;
  }, [step, form.guest_name, form.contact]);

  const goNext = () => {
    setError(null);
    if (!stepValid) {
      setError('Please fill in your name and a way to reach you before continuing.');
      return;
    }
    if (step < STEPS.length) {
      const next = step + 1;
      setStep(next);
      setFurthestStep((f) => Math.max(f, next));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setError(null);
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const jumpTo = (s) => {
    if (s <= furthestStep) {
      setStep(s);
      setError(null);
    }
  };

  async function onSubmit() {
    setError(null);
    if (!form.guest_name.trim() || !form.contact.trim()) {
      setError('Please share your name and a way to reach you.');
      setStep(1);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        guest_name: form.guest_name.trim(),
        contact: form.contact.trim(),
        email: form.email.trim() || null,
        city_of_origin: form.city_of_origin.trim() || null,
        nationality: form.nationality.trim() || null,
        requested_checkin: form.requested_checkin ? format(form.requested_checkin, 'yyyy-MM-dd') : null,
        requested_checkout: form.requested_checkout ? format(form.requested_checkout, 'yyyy-MM-dd') : null,
        pax: form.pax ? Number(form.pax) : null,
        child_count: form.child_count ? Number(form.child_count) : null,
        room_count: form.room_count ? Number(form.room_count) : null,
        bed_count_preference: form.bed_count_preference ? Number(form.bed_count_preference) : null,
        room_type: form.room_type || null,
        cities_to_visit: form.cities_to_visit.length ? form.cities_to_visit : null,
        services_needed: form.services_needed.length ? form.services_needed : null,
        notes: form.notes.trim() || null,
      };
      const res = await submitInquiry(payload);
      setSuccess(res.inquiry_code);
      toast.success(`Inquiry received — ref ${res.inquiry_code}`);
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
      toast.error('We could not submit your inquiry.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="px-6 md:px-12 lg:px-14 py-16 md:py-20 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          id="inquiry-success-block"
          data-testid={TID.inquirySuccess}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto rounded-full border-2 border-emerald-brand flex items-center justify-center mb-8">
            <Check className="text-emerald-brand" size={28} strokeWidth={1.5} />
          </div>
          <div className="eyebrow mb-4">Inquiry Received</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink leading-[1.1]">
            Thank you. We&rsquo;ll be <em className="italic text-emerald-brand">in touch shortly.</em>
          </h2>
          <div className="hairline w-16 mx-auto mt-8" />
          <p className="mt-8 text-ink-muted leading-relaxed max-w-md mx-auto">
            Your reference number is below. Our concierge will reply within the hour on the contact you shared.
          </p>
          <div className="mt-10 inline-block px-8 py-5 border border-emerald-brand/30 bg-emerald-brand/5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-gold-brand mb-2">Reference</div>
            <div className="font-serif text-2xl text-emerald-brand tracking-[0.05em]">{success}</div>
          </div>
          <div className="mt-10">
            <button
              onClick={() => { setSuccess(null); setStep(1); setFurthestStep(1); }}
              className="text-[11px] tracking-[0.22em] uppercase font-semibold text-emerald-brand link-underline"
            >
              Submit another inquiry
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid={TID.inquiryForm} className="px-6 md:px-12 lg:px-14 py-12 md:py-16 lg:py-16 max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-3">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => jumpTo(s.id)}
                disabled={s.id > furthestStep}
                data-testid={`stepper-tab-${s.id}`}
                className={`flex-1 h-1 transition-all duration-500 ${
                  s.id === step ? 'bg-emerald-brand'
                  : s.id < step || s.id <= furthestStep ? 'bg-emerald-brand/40 hover:bg-emerald-brand/60 cursor-pointer'
                  : 'bg-line cursor-not-allowed'
                }`}
                aria-label={`Step ${s.id}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-gold-brand font-semibold">
              Step {step} of {STEPS.length}
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight mt-2">
              {STEPS[step - 1].title}
            </h2>
            <p className="text-sm text-ink-muted mt-1">{STEPS[step - 1].hint}</p>
          </div>
          <div className="hidden md:block">
            <div className="font-serif text-6xl text-gold-brand leading-none">{String(step).padStart(2, '0')}</div>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className={labelClass}>Full name *</Label>
                  <Input data-testid={TID.inquiryFieldName} value={form.guest_name} onChange={(e) => set('guest_name')(e.target.value)} placeholder="As on your passport" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>Phone / WhatsApp *</Label>
                  <Input data-testid={TID.inquiryFieldContact} value={form.contact} onChange={(e) => set('contact')(e.target.value)} placeholder="+1 555 123 4567" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>Email</Label>
                  <Input data-testid={TID.inquiryFieldEmail} type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder="you@example.com" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>City / country</Label>
                  <Input data-testid={TID.inquiryFieldCity} value={form.city_of_origin} onChange={(e) => set('city_of_origin')(e.target.value)} placeholder="London, UK" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                  <Label className={labelClass}>Passport nationality <span className="opacity-50 normal-case tracking-normal">(for visa)</span></Label>
                  <Input data-testid={TID.inquiryFieldNationality} value={form.nationality} onChange={(e) => set('nationality')(e.target.value)} placeholder="British" className={inputClass} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <Label className={labelClass}>Adults</Label>
                  <Input data-testid={TID.inquiryFieldPax} type="number" min="1" max="50" value={form.pax} onChange={(e) => set('pax')(e.target.value)} placeholder="2" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>Children</Label>
                  <Input data-testid={TID.inquiryFieldChildren} type="number" min="0" max="20" value={form.child_count} onChange={(e) => set('child_count')(e.target.value)} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>Rooms</Label>
                  <Input data-testid={TID.inquiryFieldRoomCount} type="number" min="1" max="20" value={form.room_count} onChange={(e) => set('room_count')(e.target.value)} placeholder="1" className={inputClass} />
                </div>
                <div>
                  <Label className={labelClass}>Beds / room</Label>
                  <Input data-testid={TID.inquiryFieldBeds} type="number" min="1" max="6" value={form.bed_count_preference} onChange={(e) => set('bed_count_preference')(e.target.value)} placeholder="2" className={inputClass} />
                </div>
              </div>
              <div>
                <Label className={labelClass}>Room type</Label>
                <Select value={form.room_type} onValueChange={set('room_type')}>
                  <SelectTrigger data-testid={TID.inquiryFieldRoomType} className={inputClass}>
                    <SelectValue placeholder="No preference" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className={labelClass}>Check-in</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={TID.inquiryFieldCheckin} type="button" className={`${inputClass} text-left flex items-center justify-between`}>
                        <span className={form.requested_checkin ? 'text-ink' : 'text-ink-muted/40'}>
                          {form.requested_checkin ? format(form.requested_checkin, 'd MMM yyyy') : 'Arrival'}
                        </span>
                        <span className="text-gold-brand text-[10px] tracking-[0.2em] uppercase">Date</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-line" align="start">
                      <Calendar mode="single" selected={form.requested_checkin} onSelect={set('requested_checkin')} disabled={(d) => d < new Date(Date.now() - 86400000)} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className={labelClass}>Check-out</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={TID.inquiryFieldCheckout} type="button" className={`${inputClass} text-left flex items-center justify-between`}>
                        <span className={form.requested_checkout ? 'text-ink' : 'text-ink-muted/40'}>
                          {form.requested_checkout ? format(form.requested_checkout, 'd MMM yyyy') : 'Departure'}
                        </span>
                        <span className="text-gold-brand text-[10px] tracking-[0.2em] uppercase">Date</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-line" align="start">
                      <Calendar mode="single" selected={form.requested_checkout} onSelect={set('requested_checkout')} disabled={(d) => d <= (form.requested_checkin || new Date(Date.now() - 86400000))} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <Label className={labelClass}>Holy cities</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {CITIES.map((c) => {
                    const checked = form.cities_to_visit.includes(c.value);
                    return (
                      <label key={c.value} data-testid={c.tid}
                        className={`flex items-center gap-2.5 px-3 py-3 border cursor-pointer transition-colors ${checked ? 'border-emerald-brand bg-emerald-brand/5' : 'border-line bg-white hover:border-emerald-brand/40'}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleArr('cities_to_visit', c.value)} className="data-[state=checked]:bg-emerald-brand data-[state=checked]:border-emerald-brand" />
                        <span className="font-serif text-[15px] text-ink">{c.value}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className={labelClass}>Services needed</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => {
                    const checked = form.services_needed.includes(s.value);
                    return (
                      <label key={s.value} data-testid={s.tid}
                        className={`flex items-center gap-2.5 px-3 py-3 border cursor-pointer transition-colors ${checked ? 'border-emerald-brand bg-emerald-brand/5' : 'border-line bg-white hover:border-emerald-brand/40'}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleArr('services_needed', s.value)} className="data-[state=checked]:bg-emerald-brand data-[state=checked]:border-emerald-brand" />
                        <span className="font-serif text-[15px] text-ink">{s.value}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-7">
              <div>
                <Label className={labelClass}>Notes</Label>
                <Textarea data-testid={TID.inquiryFieldNotes} value={form.notes} onChange={(e) => set('notes')(e.target.value)} placeholder="Travelling with elderly parents · Late arrival · Specific dietary needs · Anything we should know..." rows={5} className={`${inputClass} !py-3`} />
              </div>

              {/* Review summary */}
              <div className="mt-8 border border-line bg-white/50">
                <div className="px-5 py-3 border-b border-line eyebrow !text-ink-muted">Review your details</div>
                <ReviewRow label="Name" value={form.guest_name} />
                <ReviewRow label="Contact" value={form.contact} />
                <ReviewRow label="Email" value={form.email} />
                <ReviewRow label="From" value={form.city_of_origin} />
                <ReviewRow label="Passport" value={form.nationality} />
                <ReviewRow label="Group" value={[form.pax && `${form.pax} adults`, form.child_count && `${form.child_count} children`].filter(Boolean).join(' · ')} />
                <ReviewRow label="Rooms" value={[form.room_count && `${form.room_count} rooms`, form.bed_count_preference && `${form.bed_count_preference} beds/room`, form.room_type].filter(Boolean).join(' · ')} />
                <ReviewRow label="Dates" value={[form.requested_checkin && format(form.requested_checkin, 'd MMM yyyy'), form.requested_checkout && format(form.requested_checkout, 'd MMM yyyy')].filter(Boolean).join(' → ')} />
                <ReviewRow label="Cities" value={form.cities_to_visit.join(', ')} />
                <ReviewRow label="Services" value={form.services_needed.join(', ')} last />
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {error && (
        <div data-testid={TID.inquiryError} className="mt-6 p-4 border border-red-300 bg-red-50 text-red-800 text-sm flex items-start gap-3">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Footer nav */}
      <div className="mt-10 pt-8 border-t border-line flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 1}
          data-testid="stepper-back"
          className={`inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
            step === 1 ? 'text-ink-muted/30 cursor-not-allowed' : 'text-emerald-brand hover:text-emerald-deep'
          }`}
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </button>

        {step < STEPS.length ? (
          <button
            type="button"
            onClick={goNext}
            data-testid="stepper-next"
            className="btn-primary"
          >
            Continue <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={loading}
            data-testid={TID.inquirySubmit}
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? 'Sending...' : 'Send Inquiry'}
          </button>
        )}
      </div>

      <p className="mt-5 text-[11px] text-ink-muted/70 text-center">
        Your details stay with us · No payment required to inquire
      </p>
    </div>
  );
}

function ReviewRow({ label, value, last }) {
  return (
    <div className={`flex items-baseline justify-between gap-6 px-5 py-3 ${last ? '' : 'border-b border-line/60'}`}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-ink-muted font-semibold flex-shrink-0">{label}</div>
      <div className="font-serif text-sm md:text-base text-ink text-right truncate max-w-[60%]">
        {value || <span className="text-ink-muted/40 italic">—</span>}
      </div>
    </div>
  );
}
